export const TIER_LIMITS = {
  free: {
    projects: 3,
    endpointsPerProject: 3,
    rulesPerEndpoint: 3,
    // Max seats on a team owned by this tier (owner counts as a seat).
    // Free users can't own a real team, so effectively just themselves.
    teamMembers: 1,
    // Max teams this tier may own. Free users can't own a real team.
    maxTeams: 0,
    globalVariables: 0,
    projectVariables: 0,
    requestsPerDay: 500,
    logRetentionDays: 1,
    maxResponseSize: 64 * 1024, // 64KB
    maxRequestSize: 100 * 1024, // 100KB
    maxDelay: 5000, // 5 seconds
    defaultEndpointRateLimit: 30, // requests per minute
    maxEndpointRateLimit: 60,
  },
  pro: {
    projects: 10,
    endpointsPerProject: 10,
    rulesPerEndpoint: 15,
    teamMembers: 5,
    maxTeams: 1,
    globalVariables: 50,
    projectVariables: 25,
    requestsPerDay: 10000,
    logRetentionDays: 3,
    maxResponseSize: 1024 * 1024, // 1MB
    maxRequestSize: 512 * 1024, // 512KB
    maxDelay: 15000, // 15 seconds
    defaultEndpointRateLimit: 60, // requests per minute
    maxEndpointRateLimit: 120,
  },
  business: {
    projects: 25,
    endpointsPerProject: 25,
    rulesPerEndpoint: 25,
    teamMembers: 25,
    maxTeams: 100, // effectively unlimited
    globalVariables: 100,
    projectVariables: 50,
    requestsPerDay: 100000,
    logRetentionDays: 14,
    maxResponseSize: 2 * 1024 * 1024, // 2MB
    maxRequestSize: 2 * 1024 * 1024, // 2MB
    maxDelay: 30000, // 30 seconds
    defaultEndpointRateLimit: 100, // requests per minute
    maxEndpointRateLimit: 500,
  },
} as const;

export type Tier = keyof typeof TIER_LIMITS;

export const TIER_LABELS: Record<Tier, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
};

/**
 * How long an unclaimed (anonymous) project lives, measured from creation. Past
 * this the API stops serving it and the hourly reaper deletes it and its DO.
 */
export const ANONYMOUS_PROJECT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Fractions of a project's daily request quota at which the endpoint DO notifies
 * its owners. A third alert fires on the first request rejected past the limit.
 */
export const USAGE_ALERT_THRESHOLDS = {
  usage_80: 0.8,
  usage_95: 0.95,
} as const;

/**
 * Monthly list price in USD per tier. Single source of truth for price copy —
 * both the public pricing page and the in-app billing page read from here.
 */
export const TIER_PRICING = {
  free: { monthly: 0 },
  pro: { monthly: 10 },
  business: { monthly: 30 },
} as const satisfies Record<Tier, { monthly: number }>;

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;
export type HttpMethod = typeof HTTP_METHODS[number];
